import type { FlexProps } from '@chakra-ui/react';
import { Flex, useCallbackRef } from '@chakra-ui/react';
import { SlideButton } from '@ui/components/SlideButton';
import { useResizeObserver } from '@ui/hooks/useResizeObserver';
import { useEffect, useState } from 'react';

export type HorizontalScrollingButtonProps = FlexProps & {
  scrollContainer: HTMLElement | null | undefined;
};
export const HorizontalScrollingButton = ({
  scrollContainer,
  ...props
}: HorizontalScrollingButtonProps) => {
  const [isPrevDisabled, setIsPrevDisabled] = useState(true);
  const [isNextDisabled, setIsNextDisabled] = useState(true);

  const updateButtonState = useCallbackRef(() => {
    if (!scrollContainer) {
      return;
    }
    const noScrollbar =
      scrollContainer.clientWidth === scrollContainer.scrollWidth;
    setIsPrevDisabled(noScrollbar || !scrollContainer.scrollLeft);
    setIsNextDisabled(
      noScrollbar ||
        scrollContainer.scrollLeft + scrollContainer.clientWidth >=
          scrollContainer.scrollWidth,
    );
  });

  useResizeObserver({
    dom: scrollContainer,
    handler: updateButtonState,
  });

  useEffect(() => {
    if (!scrollContainer) {
      return;
    }
    scrollContainer.addEventListener('scroll', updateButtonState);
    return () => {
      scrollContainer.removeEventListener('scroll', updateButtonState);
    };
  }, [scrollContainer, updateButtonState]);

  return (
    <Flex {...props} justify="end" mb="10px">
      <SlideButton
        isPrevDisabled={isPrevDisabled}
        isNextDisabled={isNextDisabled}
        onPrev={() => {
          if (scrollContainer) {
            scrollContainer.scrollTo({
              left: scrollContainer.scrollLeft - scrollContainer.clientWidth,
              behavior: 'smooth',
            });
          }
        }}
        onNext={() => {
          if (scrollContainer) {
            scrollContainer.scrollTo({
              left: scrollContainer.scrollLeft + scrollContainer.clientWidth,
              behavior: 'smooth',
            });
          }
        }}
      />
    </Flex>
  );
};
